import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Info, ArrowRight, Heart } from "lucide-react";
import client, { errText } from "../lib/api";
import { ConsentNoticeDialog } from "../components/ConsentNoticeDialog";

export default function ConsentScreen({ product }) {
  const nav = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const p = await client.get("/consent/active-policy");
      setPolicy(p.data);
      const s = await client.get("/consent/status");
      setStatus(s.data);
    } catch (e) {
      setErr(errText(e, "We could not load your consent state."));
    }
  };
  useEffect(() => { load(); }, []);

  const accept = async () => {
    if (!policy) return;
    setLoading(true); setErr("");
    try {
      await client.post("/consent/data-processing/accept", { policy_version: policy.policy_version });
      // Optimistic local flip so the CTA unlocks immediately. The
      // server-side gate is still the real one on the next request.
      setStatus((prev) => ({
        ...(prev || {}),
        data_processing: { accepted: true, policy_version: policy.policy_version },
      }));
    } catch (e) {
      setErr(errText(e, "That did not go through."));
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (!status) return false;
    if (!status.data_processing?.accepted) return false;
    if (status.person?.is_minor) {
      return status.guardian_consent?.status === "active";
    }
    return true;
  };

  return (
    <div className="k-shell relative overflow-hidden">
      <div className="k-orb k-orb-blue" style={{ top: "-10rem", left: "40%", width: "20rem", height: "20rem" }} />
      <div className="k-orb k-orb-amber" style={{ bottom: "-6rem", right: "-4rem", width: "18rem", height: "18rem" }} />

      <div className="relative max-w-3xl mx-auto px-5 pt-12 pb-24">
        <div className="text-center k-fade-in">
          <span className="k-chip k-chip-teal mb-3 mx-auto"><Heart size={12} /> a warm hello</span>
          <h1 className="k-hero-title mb-3" style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)" }}>
            One quick check, <span style={{ color: "hsl(var(--klareet-blue))" }}>then Klair takes over</span>
          </h1>
          <p className="k-hero-sub mx-auto">
            Your answers stay yours. Only you ever see them. Your college only ever sees group totals, never you.
          </p>
        </div>

        <div className="mt-10 space-y-5">
          <div className="k-card k-lift k-fade-in overflow-hidden" style={{ padding: 0, animationDelay: "60ms" }}>
            <div className="k-spectrum-bar" />
            <div className="p-6 sm:p-7">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
                     style={{ background: "hsl(var(--klareet-teal-soft))", color: "hsl(var(--klareet-teal))" }}>
                  <ShieldCheck size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl klareet-serif mb-1">How Klareet uses your information</h3>
                  <p className="k-quiet mb-2">
                    Version <span className="klareet-mono">{policy?.policy_version || "loading"}</span>
                  </p>
                  <p className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))" }}>{policy?.summary}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    {status?.data_processing?.accepted ? (
                      <span className="k-chip k-chip-teal" data-testid="dp-accepted">Accepted, thank you</span>
                    ) : (
                      <button onClick={accept} disabled={loading} className="k-btn k-btn-primary" data-testid="dp-accept">
                        {loading ? "Saving." : "I agree, continue"}
                      </button>
                    )}
                    <ConsentNoticeDialog version={policy?.policy_version} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {status?.person?.is_minor && (
            <div className="k-warm-card p-6 k-fade-in" style={{ animationDelay: "120ms" }}>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
                     style={{ background: "hsl(var(--klareet-amber-soft))", color: "hsl(var(--klareet-amber))" }}>
                  <Info size={20} />
                </div>
                <div>
                  <h3 className="text-xl klareet-serif mb-1">Guardian consent</h3>
                  <p className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
                    A verified guardian sign-off is required for anyone under eighteen.
                    Ask your guardian to complete the guardian link we sent.
                  </p>
                </div>
              </div>
            </div>
          )}

          {err && <div className="k-quiet" style={{ color: "hsl(var(--destructive))" }}>{err}</div>}

          <div className="text-center pt-3">
            <button
              onClick={() => nav(product === "grow" ? "/grow/welcome" : "/welcome")}
              disabled={!canProceed()}
              className="k-btn k-btn-accent"
              data-testid="consent-continue"
            >
              Start the conversation <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
